import { useCallback, useState } from "react";

const read = <T,>(key: string, initial: T): T => {
    try {
        const raw = localStorage.getItem(key);
        return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
        return initial;
    }
};

/** Per-viewer UI preference; falls back to the default when storage is blocked or holds junk. */
export const useLocalStorage = <T,>(key: string, initial: T): [T, (value: T) => void] => {
    const [stored, setStored] = useState<{ key: string; value: T }>(() => ({ key, value: read(key, initial) }));
    // A new key (another campaign or organisation) reads its own value instead of keeping the previous key's.
    const value = stored.key === key ? stored.value : read(key, initial);
    if (stored.key !== key) setStored({ key, value });

    const update = useCallback(
        (next: T) => {
            setStored({ key, value: next });
            try {
                localStorage.setItem(key, JSON.stringify(next));
            } catch {
                // Storage unavailable: keep the in-memory value for this visit.
            }
        },
        [key],
    );

    return [value, update];
};
